import apiClient from "../utils/axiosConfig";
import { tokenRefreshService } from "./tokenRefreshService";
import { getApiUrl, API_ENDPOINTS } from "../Configurations/config";
import { getUserId } from "../utils/userUtils";
/**
 * Chat Service - Handles all chat and query related API calls
 */
class ChatService {
  /**
   * Send a query to the chatbot
   * @param {string} query - User query text
   * @param {string} sessionId - Session ID
   * @param {string} userId - User ID (optional), must match value used in store-token for backend lookup
   * @param {Object} options - Additional options (debug, cache_debug, pagination_enabled, trace_metrics)
   * @returns {Promise} API response
   */
  async sendQuery(query, sessionId, userId = null, options = {}, requestId = null, abortSignal = null) {
    try {
      const uid = userId != null && userId !== "" ? userId : getUserId() || "anonymous";
      const databricksToken = await tokenRefreshService.ensureDatabricksToken({ interactive: true });
      const requestBody = {
        query,
        session_id: sessionId,
        user_id: uid,
        debug: options.debug || "False",
        cache_debug: options.cache_debug || "False",
        pagination_enabled: options.pagination_enabled || "true",
        trace_metrics: options.trace_metrics || "false",
        databricks_token: databricksToken || "",
        query_mode: options.query_mode || "qa"
      };

      if (requestId) {
        requestBody.request_id = requestId;
      }

      const response = await apiClient.post(
        getApiUrl(API_ENDPOINTS.QUERY.BASE),
        requestBody,
        abortSignal ? { signal: abortSignal } : undefined
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Execute SQL query with pagination
   * @param {string} sqlQuery - SQL query to execute
   * @param {number} offset - Offset for pagination
   * @param {number} limit - Limit for pagination
   * @param {string} sessionId - Session ID
   * @param {string} userId - User ID (optional, from login)
   * @returns {Promise} API response
   */
  async executeSql(sqlQuery, offset = 0, limit = 25, sessionId = null, userId = null) {
    try {
      const databricksToken = await tokenRefreshService.ensureDatabricksToken({ interactive: true });

      // If the SQL contains a LIMIT clause anywhere, use that value as the request limit
      const sqlLimitMatch = sqlQuery && sqlQuery.match(/\bLIMIT\s+(\d+)/i);
      const resolvedLimit = sqlLimitMatch ? Number(sqlLimitMatch[1]) : limit;

      const requestBody = {
        sql_query: sqlQuery,
        offset,
        limit: resolvedLimit,
        databricks_token: databricksToken || ""
      };

      if (sessionId) {
        requestBody.session_id = sessionId;
      }

      if (userId) {
        requestBody.user_id = userId;
      }

      const response = await apiClient.post(
        getApiUrl(API_ENDPOINTS.QUERY.EXECUTE_SQL),
        requestBody
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get visualization suggestions for query results.
   * Request body matches query endpoint: query, session_id, user_id, plus visualize-specific fields.
   * @param {string} userQuery - Original user query
   * @param {string} sqlQuery - Generated SQL query
   * @param {Array} queryResults - Results array from query API
   * @param {string} sessionId - Session ID
   * @param {string} messageId - Message ID
   * @param {string} userId - User ID (optional, same as query API)
   * @returns {Promise} API response with chart suggestions
   */
  async getVisualization(userQuery, sqlQuery, queryResults, sessionId, messageId, userId = null) {
    try {
      const columns = queryResults.length > 0 ? Object.keys(queryResults[0]) : [];
      const databricksToken = await tokenRefreshService.ensureDatabricksToken({ interactive: true });
      const requestBody = {
        query: userQuery,
        session_id: sessionId,
        user_query: userQuery,
        sql_query: sqlQuery,
        query_results: {
          success: true,
          columns: columns,
          results: queryResults
        },
        message_id: messageId,
        databricks_token: databricksToken || ""
      };

      if (userId) {
        requestBody.user_id = userId;
      }

      const response = await apiClient.post(
        getApiUrl(API_ENDPOINTS.QUERY.VISUALIZE),
        requestBody
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Export query results as CSV via backend.
   * Backend: POST /api/query/export (Authorization: Bearer <databricks_token>)
   *
   * @param {Object} params
   * @param {number} params.message_id
   * @param {string} params.session_id
   * @param {"100"|"50%"|"100%"} params.export_type
   */
  async exportQueryResults({ message_id, session_id, export_type }) {
    try {
      const url = getApiUrl(API_ENDPOINTS.QUERY.EXPORT);
      const databricksToken = await tokenRefreshService.ensureDatabricksToken({ interactive: true });
      const response = await apiClient.post(url, {
        message_id,
        session_id,
        export_type,
        databricks_token: databricksToken || "",
        msal_token: databricksToken || "",
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

}

export const chatService = new ChatService();
export default chatService;

